import React, { useMemo, useState, useEffect, useRef } from 'react';
import type { Day13 } from '@/lib/calendar13';
import type { Month13 } from '@/lib/calendar13Types';
import { WEEKDAY_NAMES } from '@/lib/calendar13Types';
import { cn } from '@/lib/utils';
import { Calendar } from 'lucide-react';
import { EventBadge } from '../calendar/EventBadge';
import { useEventsForRange, useEventPriorityBadge } from '@/hooks/useEventsForRange';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface ShortGridWidgetProps {
  currentMonth: Month13 | null;
  selectedDay: Day13 | null;
  displayDay: Day13;
  today: Day13;
  selectedMonth: number;
  onSelectDay?: (day: Day13) => void;
  className?: string;
  variant?: 'grid' | 'row';
  dashboardId?: string;
}

export function ShortGridWidget({
  currentMonth,
  selectedDay,
  displayDay,
  today,
  selectedMonth,
  onSelectDay,
  className,
  variant = 'row',
  dashboardId,
}: ShortGridWidgetProps) {
  const [showDateDialog, setShowDateDialog] = useState(false);
  const [revealedDayCount, setRevealedDayCount] = useState(0);
  const rowRef = useRef<HTMLDivElement>(null);
  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  
  // Fetch events for the current month
  const monthStart = currentMonth?.days[0]?.gregorianDate ?? new Date();
  const monthEnd = currentMonth?.days[currentMonth.days.length - 1]?.gregorianDate ?? new Date();
  const eventsByDate = useEventsForRange(monthStart, monthEnd, dashboardId);
  
  const gridDays = useMemo(() => {
    if (!currentMonth?.days) return [];
    return currentMonth.days.slice(0, 28);
  }, [currentMonth]);

  const isDrift = selectedMonth === 14;
  const currentWeekdayName = WEEKDAY_NAMES[displayDay?.weekday ?? 0] ?? '';

  // Auto-scroll to selected day
  useEffect(() => {
    if (!rowRef.current || variant !== 'row' || !displayDay) return;
    const el = rowRef.current.querySelector<HTMLButtonElement>(`button[data-day="${displayDay.month}-${displayDay.day}"]`);
    if (el) {
      const scrollParent = rowRef.current;
      const containerWidth = scrollParent.clientWidth;
      const elementLeft = el.offsetLeft;
      const elementWidth = el.offsetWidth;
      const targetScroll = elementLeft - (containerWidth / 2) + (elementWidth / 2);
      scrollParent.scrollTo({ left: Math.max(0, targetScroll), behavior: 'smooth' });
    }
  }, [displayDay, variant]);

  useEffect(() => {
    if (isDrift || gridDays.length === 0) {
      setRevealedDayCount(0);
      return;
    }
    setRevealedDayCount(1);
    const timer = setInterval(() => {
      setRevealedDayCount((prev) => {
        if (prev >= gridDays.length) {
          clearInterval(timer);
          return prev;
        }
        return prev + 1;
      });
    }, 36);
    return () => clearInterval(timer);
  }, [gridDays, isDrift]);

  return (
    <>
      <div className={cn("w-full h-full flex flex-col gap-2 px-4 py-3", className)}>
        {/* Header - Prominent day display */}
        <div className="flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-1 h-4 rounded-full bg-gradient-to-b from-teal-400 to-teal-600" />
              <span className="text-xs font-semibold text-teal-100 tracking-wide">
                {isDrift ? '✧ Drift ✧' : (currentMonth?.name ?? 'Moon')}
              </span>
            </div>
          </div>
          
          {/* Current day highlight */}
          {!isDrift && (
            <div className="flex items-center gap-2">
              <div 
                className="px-3 py-1 rounded-lg text-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(248,250,252,0.9))',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.5)',
                }}
              >
                <div className="text-[9px] font-bold text-rose-600 uppercase tracking-wider">
                  {currentWeekdayName}
                </div>
                <div className="text-lg font-bold text-slate-800 leading-none -mt-0.5">
                  {displayDay?.day ?? 1}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Horizontal day strip */}
        {!isDrift && currentMonth && (
          <div className="flex-1 min-h-0 flex flex-col gap-1">
            <div 
              ref={rowRef} 
              className="flex-1 flex items-center gap-1 overflow-x-auto pb-1 -mx-2 px-2 scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {gridDays.map((day, index) => {
                const isToday = today.month === day.month && today.day === day.day;
                const isCurrent = displayDay?.month === day.month && displayDay?.day === day.day;
                const isRevealed = index < revealedDayCount;
                const weekdayLabel = WEEKDAY_NAMES[day.weekday]?.slice(0, 1).toUpperCase() ?? weekDays[day.weekday] ?? '';
                const dateKey = day.gregorianDate ? day.gregorianDate.toISOString().split('T')[0] : '';
                const dayEvents = eventsByDate[dateKey] || [];
                const { color, count, statuses } = useEventPriorityBadge(dayEvents);

                return (
                  <button
                    key={index}
                    data-day={`${day.month}-${day.day}`}
                    onClick={() => onSelectDay?.(day)}
                    className={cn(
                      "relative flex flex-col items-center justify-center rounded-lg transition-all shrink-0",
                      isCurrent
                        ? "bg-white text-slate-900 shadow-lg ring-1 ring-white/50"
                        : isToday
                          ? "bg-cyan-500/20 text-cyan-100 border border-cyan-400/40"
                          : "bg-teal-900/40 text-teal-100/80 hover:bg-teal-800/50 border border-teal-700/30"
                    )}
                    style={{
                      width: isCurrent ? '44px' : '36px',
                      height: isCurrent ? '52px' : '44px',
                      transform: `scale(${isCurrent ? 1.05 : 1}) translateY(${isRevealed ? 0 : 6}px)`,
                      opacity: isRevealed ? 1 : 0,
                      zIndex: isCurrent ? 10 : 0,
                    }}
                  >
                    {/* Weekday label */}
                    <span className={cn(
                      "text-[8px] font-medium uppercase tracking-wider",
                      isCurrent ? "text-rose-500" : "text-current opacity-60"
                    )}>
                      {weekdayLabel}
                    </span>
                    {/* Day number */}
                    <span className={cn(
                      "font-bold tabular-nums leading-none",
                      isCurrent ? "text-base" : "text-sm"
                    )}>
                      {day.day}
                    </span>
                    {/* Event indicator */}
                    {count > 0 && (
                      <div 
                        className="absolute -bottom-0.5 w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: color || '#f4d03f' }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Quick actions */}
            <div className="flex items-center justify-between text-[9px] text-teal-300/50 shrink-0 pt-1 border-t border-teal-400/10">
              <span>28-day moon</span>
              <button 
                onClick={() => onSelectDay?.(today)}
                className="text-teal-300/70 hover:text-teal-200 transition-colors"
              >
                Today
              </button>
            </div>
          </div>
        )}

        {/* Drift display */}
        {isDrift && (
          <div className="flex-1 min-h-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-2xl mb-1">✧</div>
              <div className="text-[10px] text-amber-200/60">Year transition portal</div>
            </div>
          </div>
        )}
      </div>

      {/* Dialog kept for potential future use */}
      <Dialog open={showDateDialog} onOpenChange={setShowDateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select a day</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-7 gap-1">
            {gridDays.map((day) => {
              const isToday = today.month === day.month && today.day === day.day;
              const isCurrent = displayDay?.month === day.month && displayDay?.day === day.day;
              return (
                <button
                  key={`${day.month}-${day.day}`}
                  onClick={() => {
                    onSelectDay?.(day);
                    setShowDateDialog(false);
                  }}
                  className={cn(
                    "flex items-center justify-center rounded text-xs px-2 py-2 transition-all font-medium",
                    isCurrent
                      ? "bg-primary text-primary-foreground"
                      : isToday
                        ? "bg-cyan-500/15 text-cyan-100 border border-cyan-400/40"
                        : "bg-background/60 text-foreground/70 hover:bg-secondary/50 border border-border/50"
                  )}
                >
                  {day.day}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
